import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, LIGHT_BG, PRIMARY_BLUE, withOpacity} from '../../../../styles/foundation/colors'
import {RADIUS, GAP} from '../../../../styles/foundation/spacing'
import {kg} from '../format'

/** One tile: label on top, value, then a short caption. Module-scope, not
 * redeclared per render — same pattern as SopPage.js's SopBlock/SectionLabel. */
function WeightFigure({label, value, muted, bold, valueSize, caption, highlight}) {
  return (
    <Box
      sx={{
        display: 'grid', alignContent: 'start', gap: 0.25, minWidth: 0,
        p: GAP.sm, borderRadius: RADIUS.md,
        border: `1px solid ${highlight ? withOpacity(PRIMARY_BLUE, 0.45) : BORDER}`,
        bgcolor: highlight ? withOpacity(PRIMARY_BLUE, 0.06) : LIGHT_BG,
      }}
    >
      <Typography sx={{
        fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE,
        textTransform: 'uppercase', letterSpacing: '0.03em',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontFamily: FONT, fontSize: valueSize, fontVariantNumeric: 'tabular-nums',
        fontWeight: bold ? WEIGHT.bold : WEIGHT.semibold,
        color: highlight ? PRIMARY_BLUE : (muted ? SUBTLE : NAVY),
        lineHeight: 1.3,
      }}>
        {value}
      </Typography>
      {caption && (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.4}}>
          {caption}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Dead / volumetric / chargeable, as three equal tiles. The governing figure
 * is the greater of the two — a TIE reads as volumetric-governed, matching
 * weightMath's own `chargeableKg` (`Math.max`) and the back-solved dataset,
 * which does produce ties. The loser is muted; chargeable is always bold and
 * carries the tinted border, since it is the one figure that actually bills
 * and the one a reviewer is looking for.
 *
 * The columns are `repeat(3, 1fr)`, not `auto-fit` — auto-fit reflowed to a
 * ragged 2 + 1 between roughly 300 and 440px, which is exactly the width a
 * card column lands on. One breakpoint instead: stacked on a phone, three
 * equal tiles from `sm` up.
 *
 * `compact` shrinks the value from SIZE.lg to SIZE.base for the in-card use
 * on the review/reject cards, where three full-size figures would crowd a
 * card that already carries a timeline alongside.
 */
export default function RqWeights({req, compact = false}) {
  const deadKg = (req && req.deadKg) || 0
  const volKg = (req && req.volKg) || 0
  const chargeKg = (req && req.chargeKg) || 0
  const volGoverns = volKg >= deadKg
  const valueSize = compact ? SIZE.base : SIZE.lg

  return (
    <Box>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))'}, gap: GAP.sm}}>
        <WeightFigure
          label='Dead weight' value={kg(deadKg)} valueSize={valueSize}
          muted={volGoverns} caption={volGoverns ? 'below' : 'governs'}
        />
        <WeightFigure
          label='Volumetric weight' value={kg(volKg)} valueSize={valueSize}
          muted={!volGoverns} caption={volGoverns ? 'governs' : 'below'}
        />
        <WeightFigure
          label='Chargeable weight' value={kg(chargeKg)} valueSize={valueSize}
          bold highlight caption='the higher of the two — this is what bills'
        />
      </Box>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: GAP.xs, lineHeight: 1.5}}>
        Chargeable weight is the measured figure. The dead/volumetric split behind it is a modelled
        breakdown, not a separate courier reading.
      </Typography>
    </Box>
  )
}
